import { cloneDeep } from 'lodash-es';

import { IGroupFilterItem } from '@/bundles/GroupFilters/helpers/getDefaultFiltersObj';
import { IOldFiltersFromSet } from '@/bundles/GroupFilters/interfaces/mappers/IOldFiltersFromSet';
import {
  getLandSizeRangeFormattedValue,
  getSalePriceRangeFormattedValue
} from '@/bundles/GroupFilters/helpers/formatters/inputs';
import { FREE_ISOCHRONE_FILTER_KEY, MARKER_ISOCHRONE_FILTER_KEY } from '@/bundles/Map/constants';
import { IFilterIsochroneOptions } from '@/bundles/GroupFilters/interfaces/IStoreFilterItem';

const arrKeysWithAdditionalInfo = ['land_size', 'sales_price', MARKER_ISOCHRONE_FILTER_KEY, FREE_ISOCHRONE_FILTER_KEY]; // parsing by end of key

export const mapFiltersWithAdditionalInfoFromFilterSets = ({ oldFiltersGroup, mappedFiltersGroup }: {
  oldFiltersGroup: IOldFiltersFromSet[],
  mappedFiltersGroup: IGroupFilterItem
}): IGroupFilterItem => {
  const isNeedToAddAdditionalInfo = Object.keys(mappedFiltersGroup).some((key) => {
    return arrKeysWithAdditionalInfo.some((arrKey) => key.endsWith(arrKey));
  });

  if (!isNeedToAddAdditionalInfo) {
    return mappedFiltersGroup;
  }

  const mappedFiltersGroupCopy = cloneDeep(mappedFiltersGroup) as IGroupFilterItem;

  const landSizeUnit = oldFiltersGroup.find((filter) => filter.name.endsWith('land_size_unit'));
  if (landSizeUnit) {
    const landSizeKey = landSizeUnit.name.replace('_unit', ''); // landSizeUnit.name - for example 'property.land_size'
    mappedFiltersGroupCopy[landSizeKey] = {
      ...mappedFiltersGroupCopy[landSizeKey],
      options: {
        ...mappedFiltersGroupCopy[landSizeKey].options,
        unit: landSizeUnit.value,
      },
      formattedValue: getLandSizeRangeFormattedValue(mappedFiltersGroupCopy[landSizeKey].value, landSizeUnit.value),
    };
  }

  const salesPriceUnit = oldFiltersGroup.find((filter) => filter.name.endsWith('sales_price_unit'));
  if (salesPriceUnit) {
    const salesPriceKey = salesPriceUnit.name.replace('_unit', ''); // salesPriceUnit.name - for example 'property.sales_price'
    mappedFiltersGroupCopy[salesPriceKey] = {
      ...mappedFiltersGroupCopy[salesPriceKey],
      options: {
        ...mappedFiltersGroupCopy[salesPriceKey].options,
        unit: salesPriceUnit.value,
      },
      formattedValue: getSalePriceRangeFormattedValue(mappedFiltersGroupCopy[salesPriceKey].value, salesPriceUnit.value),
    }
  }

  const markerIsochroneRouting = oldFiltersGroup.find((filter) => filter.name.endsWith('marker_isochrone_routing'));
  const markerIsochroneContourType = oldFiltersGroup.find((filter) => filter.name.endsWith('marker_isochrone_contour_type'));
  const markerIsochroneContour = oldFiltersGroup.find((filter) => filter.name.endsWith('marker_isochrone_contour'));
  if (markerIsochroneRouting && markerIsochroneContourType && markerIsochroneContour) {
    mappedFiltersGroupCopy[MARKER_ISOCHRONE_FILTER_KEY] = {
      ...mappedFiltersGroupCopy[MARKER_ISOCHRONE_FILTER_KEY],
      options: {
        ...mappedFiltersGroupCopy[MARKER_ISOCHRONE_FILTER_KEY].options,
        routingProfile: markerIsochroneRouting.value,
        contourType: markerIsochroneContourType.value,
        contour: markerIsochroneContour.value,
      } as IFilterIsochroneOptions,
    };
  }

  const freeIsochroneRouting = oldFiltersGroup.find((filter) => filter.name.endsWith('free_isochrone_routing'));
  const freeIsochroneContourType = oldFiltersGroup.find((filter) => filter.name.endsWith('free_isochrone_contour_type'));
  const freeIsochroneContour = oldFiltersGroup.find((filter) => filter.name.endsWith('free_isochrone_contour'));
  if (freeIsochroneRouting && freeIsochroneContourType && freeIsochroneContour) {
    mappedFiltersGroupCopy[FREE_ISOCHRONE_FILTER_KEY] = {
      ...mappedFiltersGroupCopy[FREE_ISOCHRONE_FILTER_KEY],
      options: {
        ...mappedFiltersGroupCopy[FREE_ISOCHRONE_FILTER_KEY].options,
        routingProfile: freeIsochroneRouting.value,
        contourType: freeIsochroneContourType.value,
        contour: freeIsochroneContour.value,
      } as IFilterIsochroneOptions,
    };
  }

  return mappedFiltersGroupCopy;
}
